import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { incidentApi, type IncidentListParams } from '../api/client'

export const incidentKeys = {
  all: ['incidents'] as const,
  taxonomy: () => [...incidentKeys.all, 'taxonomy'] as const,
  list: (params: { type: 'kanban' | 'list' | 'mine' | 'by-shift' | 'by-guard'; filters?: object }) =>
    [...incidentKeys.all, 'list', params] as const,
  detail: (id: number) => [...incidentKeys.all, 'detail', id] as const,
  locations: (filters?: Record<string, unknown>) => [...incidentKeys.all, 'locations', filters] as const,
}

export function useIncidentTaxonomy() {
  return useQuery({
    queryKey: incidentKeys.taxonomy(),
    queryFn: () => incidentApi.getTaxonomy(),
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
  })
}

export function useIncidentList(params: IncidentListParams, enabled = true) {
  return useQuery({
    queryKey: incidentKeys.list({ type: 'list', filters: params }),
    queryFn: () => incidentApi.list(params, { skipErrorToast: true }),
    staleTime: 30_000,
    placeholderData: (previous) => previous,
    enabled,
  })
}

export function useIncidentsKanban(
  filters: IncidentListParams,
  { skipErrorToast = false }: { skipErrorToast?: boolean } = {},
) {
  return useQuery({
    queryKey: incidentKeys.list({ type: 'kanban', filters }),
    queryFn: () => incidentApi.list({ ...filters, pageSize: 100 }, { skipErrorToast }),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: true,
    placeholderData: keepPreviousData,
  })
}

export function useIncidentsByShift(shiftId: number) {
  return useQuery({
    queryKey: incidentKeys.list({ type: 'by-shift', filters: { shiftId } }),
    queryFn: () => incidentApi.list({ shiftId, pageSize: 100 }),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    enabled: shiftId > 0,
  })
}

export function useIncidentsByGuard(guardUserId: number, pageSize = 100) {
  return useQuery({
    queryKey: incidentKeys.list({ type: 'by-guard', filters: { guardUserId, pageSize } }),
    queryFn: () => incidentApi.list({ guardUserId, pageSize }),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    enabled: guardUserId > 0,
  })
}

export function useIncidentDetail(id: number) {
  return useQuery({
    queryKey: incidentKeys.detail(id),
    queryFn: () => incidentApi.detail(id),
    staleTime: 2 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: true,
    enabled: id > 0,
  })
}

export function useIncidentLocations(filters: { statusFilter: string; fromDate?: string; toDate?: string }, enabled = true) {
  return useQuery({
    queryKey: incidentKeys.locations(filters),
    queryFn: () => incidentApi.locations(filters),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: true,
    enabled,
  })
}

export function useIncidentReportHistory(id: number) {
  return useQuery({
    queryKey: [...incidentKeys.detail(id), 'report-history'],
    queryFn: () => incidentApi.reportHistory(id),
    staleTime: 30_000,
    enabled: id > 0,
  })
}

export function useIncidentReportRevision(id: number, revisionNo: number, enabled = true) {
  return useQuery({
    queryKey: [...incidentKeys.detail(id), 'revision', revisionNo],
    queryFn: () => incidentApi.reportRevision(id, revisionNo),
    staleTime: Infinity,
    enabled: enabled && id > 0,
  })
}

export function useIncidentReportRecipients(id: number, enabled = true) {
  return useQuery({
    queryKey: [...incidentKeys.detail(id), 'report-recipients'],
    queryFn: () => incidentApi.reportRecipients(id),
    staleTime: 60_000,
    enabled: enabled && id > 0,
  })
}
